/**
 * Comment-layer data hook (BLA-70 / B1).
 *
 * Loads and mutates the anchored provider-feedback comments for one derivative
 * document via the `/api/documents/{id}/comments` REST surface. A comment is
 * additive annotation (ADR-030): creating one never touches the clinical record,
 * so this is a plain CRUD hook, not a governed-write flow. There is no
 * status/resolution lifecycle — a comment that has served its purpose is
 * deleted. Comment bodies are PHI; nothing is logged.
 */
import { useCallback, useEffect, useState } from "react";

import apiUtils, { isApiError } from "../apiUtils";
import type { CommentAnchor, DocumentComment } from "../types";
import logger from "../utils/logger";
import { timeOperation } from "../utils/perfMetrics";

/** Input for creating a comment. */
export interface NewComment {
  /** The free-text comment body. */
  body: string;
  /** The provider's "why", or null/absent. */
  rationale?: string | null;
  /** The span anchor, or null/absent for a document-level comment. */
  anchor?: CommentAnchor | null;
  /** The version the provider was viewing, or null/absent. */
  documentVersionId?: string | null;
}

/** A partial edit to an existing comment. */
export interface CommentEdit {
  /** The new body, if changing it. */
  body?: string;
  /** The new rationale (present-and-null clears it; absent leaves it). */
  rationale?: string | null;
}

/** The surface returned by {@link useComments}. */
export interface UseCommentsResult {
  /** The document's comments, in creation order. */
  comments: DocumentComment[];
  /** True while the initial/refresh load is in flight. */
  loading: boolean;
  /** A plain-language load error, or null. */
  error: string | null;
  /** Re-fetch the comment list. */
  reload: () => Promise<void>;
  /** Create a comment; resolves to it, or null on failure. */
  createComment: (input: NewComment) => Promise<DocumentComment | null>;
  /** Edit a comment's body/rationale; resolves to it, or null on failure. */
  updateComment: (
    commentId: string,
    edit: CommentEdit,
  ) => Promise<DocumentComment | null>;
  /** Delete a comment; resolves true on success. */
  deleteComment: (commentId: string) => Promise<boolean>;
}

/** The comments collection endpoint for a document. */
function commentsBase(documentId: string): string {
  return `/api/documents/${encodeURIComponent(documentId)}/comments`;
}

/** One comment's endpoint. */
function commentUrl(documentId: string, commentId: string): string {
  return `${commentsBase(documentId)}/${encodeURIComponent(commentId)}`;
}

/**
 * Manage the comment list for one document.
 *
 * @param documentId - The derivative document whose comments to manage.
 * @returns The comment list plus create/update/delete/reload actions.
 */
export function useComments(documentId: string): UseCommentsResult {
  const [comments, setComments] = useState<DocumentComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Public reload triggers a re-fetch by bumping the token the load effect
  // depends on (rather than calling setState directly, which the effect owns).
  const reload = useCallback(async (): Promise<void> => {
    setRefreshToken((t) => t + 1);
  }, []);

  useEffect(() => {
    let active = true;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      const resp = await apiUtils.fetchData<DocumentComment[]>(
        commentsBase(documentId),
      );
      if (!active) return;
      if (isApiError(resp)) {
        setError("Unable to load comments.");
        logger.devOnly.warn("useComments: load failed");
      } else {
        setComments(resp);
      }
      setLoading(false);
    }
    void load();
    return () => {
      active = false;
    };
  }, [documentId, refreshToken]);

  const createComment = useCallback(
    async (input: NewComment): Promise<DocumentComment | null> => {
      // BLA-348: "add a comment".
      const resp = await timeOperation(
        "comments.create",
        () =>
          apiUtils.fetchData<DocumentComment>(
            commentsBase(documentId),
            "POST",
            {
              body: input.body,
              rationale: input.rationale ?? null,
              anchor: input.anchor ?? null,
              document_version_id: input.documentVersionId ?? null,
            },
          ),
        (result) => ({ outcome: isApiError(result) ? "error" : "ok" }),
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useComments: create failed");
        return null;
      }
      setComments((prev) => [...prev, resp]);
      return resp;
    },
    [documentId],
  );

  const updateComment = useCallback(
    async (
      commentId: string,
      edit: CommentEdit,
    ): Promise<DocumentComment | null> => {
      const payload: Record<string, unknown> = {};
      if (edit.body !== undefined) payload.body = edit.body;
      if ("rationale" in edit) payload.rationale = edit.rationale ?? null;
      const resp = await apiUtils.fetchData<DocumentComment>(
        commentUrl(documentId, commentId),
        "PATCH",
        payload,
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useComments: update failed");
        return null;
      }
      setComments((prev) => prev.map((c) => (c.id === commentId ? resp : c)));
      return resp;
    },
    [documentId],
  );

  const deleteComment = useCallback(
    async (commentId: string): Promise<boolean> => {
      const resp = await apiUtils.fetchData<Record<string, never>>(
        commentUrl(documentId, commentId),
        "DELETE",
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useComments: delete failed");
        return false;
      }
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      return true;
    },
    [documentId],
  );

  return {
    comments,
    loading,
    error,
    reload,
    createComment,
    updateComment,
    deleteComment,
  };
}
